import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { spacing } from '../../design-system/tokens/spacing';
import { hexToRgba } from '../../utils/colorUtils';

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.textSecondary};
  padding: ${spacing[1]};
  transition: all 0.2s ease;
  border-radius: ${spacing[1]};

  &:hover {
    color: ${props => props.theme.colors.icomBlue || props.theme.colors.primary};
    background: ${props => props.theme.colors.icomLightBlue || props.theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: none;
    color: ${props => props.theme.colors.icomBlue || props.theme.colors.primary};
    background: ${props => props.theme.colors.icomLightBlue || props.theme.colors.surfaceHover};
    box-shadow: 0 0 0 2px ${props => hexToRgba(props.theme.colors.icomBlue || props.theme.colors.primary, 0.2)};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export interface PasswordVisibilityToggleProps {
  visible: boolean;
  onToggle: () => void;
  /** Optional id for the control being toggled (for aria-controls). */
  controlsId?: string;
}

/**
 * Accessible show/hide control for password fields. type="button" so it never submits forms.
 */
export const PasswordVisibilityToggle: React.FC<PasswordVisibilityToggleProps> = ({
  visible,
  onToggle,
  controlsId,
}) => {
  const { t } = useTranslation();
  return (
    <ToggleButton
      type="button"
      onClick={onToggle}
      aria-label={visible ? t('login.hidePassword') : t('login.showPassword')}
      aria-pressed={visible}
      aria-controls={controlsId}
    >
      {visible ? <EyeSlashIcon aria-hidden /> : <EyeIcon aria-hidden />}
    </ToggleButton>
  );
};

export default PasswordVisibilityToggle;
